import gsap from 'gsap'
import type { TCommon } from '~/src/types/common'

/**
 * Composable for managing ticker animation
 * This separates the animation logic from the component
 */
export function useTickerAnimation() {
  const animation = ref(null)
  const GSap = ref<TCommon>(gsap)

  // Constants for animation
  const INTERVAL = 0.1
  const INTERVAL_DURATION = 0.3

  /**
   * Start the ticker animation
   */
  const startAnimation = () => {
    // Only run animation on client-side
    if (import.meta.server) return

    const speed = 0.03
    const INDEXES_CONTAINER_CLASS = '.indexes-container'
    const indexesContainer = document.querySelectorAll(INDEXES_CONTAINER_CLASS)

    if (!indexesContainer.length) return

    // If animation is already running, don't start it again
    if (animation.value) return

    const { scrollWidth } = indexesContainer[0]
    const totalWidth = scrollWidth
    const duration = Math.abs(scrollWidth * speed)
    GSap.value.set(INDEXES_CONTAINER_CLASS, {
      x: (i) => i * scrollWidth,
    })
    animation.value = GSap.value.to(INDEXES_CONTAINER_CLASS, {
      delay: 2,
      duration,
      ease: 'none',
      x: `-=${totalWidth}`,
      modifiers: {
        x: GSap.value.utils.unitize((x) => parseFloat(x) % totalWidth),
      },
      repeat: -1,
    })
  }

  /**
   * Resume the animation
   */
  const resumeAnimation = () => {
    if (import.meta.server) return
    animation.value?.resume()
  }

  /**
   * Pause the animation
   */
  const pauseAnimation = () => {
    if (import.meta.server) return
    animation.value?.pause()
  }

  /**
   * Move the ticker to the left
   */
  const moveToLeft = () => {
    // Only run on client-side
    if (import.meta.server) return

    pauseAnimation()
    GSap.value.to(animation.value, {
      duration: INTERVAL_DURATION,
      progress: `+=${INTERVAL}`,
    })
  }

  /**
   * Move the ticker to the right
   */
  const moveToRight = () => {
    // Only run on client-side
    if (import.meta.server) return

    pauseAnimation()
    const currentProgress = animation.value?.progress()

    if (currentProgress - INTERVAL < 0) {
      /* this code is to avoid a unique animation
       * basically when the user clicks on the left arrow faster
       * This animation works by dividing the duration
       * according to the progress or position, until it reaches 0.
       *
       * E.g. it takes 3s to animate 300px.
       * So, to animate 200px, we need to calculate that duration
       * by dividing that width by the total duration of the animation
       * in this example, 3 seconds.
       */
      const durationToZero = currentProgress / INTERVAL_DURATION
      const remainingDuration = INTERVAL_DURATION - durationToZero
      const remainingProgress = INTERVAL - currentProgress

      gsap
        .timeline()
        .to(animation.value, {
          duration: durationToZero,
          progress: '0',
        })
        .to(animation.value, {
          duration: 0,
          progress: '1',
        })
        .to(animation.value, {
          duration: remainingDuration,
          progress: `${1 - remainingProgress}`,
        })
    } else {
      GSap.value.to(animation.value, {
        duration: INTERVAL_DURATION,
        progress: `-=${INTERVAL}`,
      })
    }
  }

  /**
   * Clean up animation on component unmount
   */
  const cleanupAnimation = () => {
    if (import.meta.client && animation.value) {
      animation.value.kill()
      animation.value = null
    }
  }

  return {
    animation,
    startAnimation,
    resumeAnimation,
    pauseAnimation,
    moveToLeft,
    moveToRight,
    cleanupAnimation,
    INTERVAL,
    INTERVAL_DURATION,
  }
}
